import { useId } from "react";
import { POSITION_OPTIONS } from "../../../constants/positions";
import { US_STATES } from "../../../constants/usStates";
import { DIVISIONS } from "../../../constants/divisions";
import {
  formatPhoneNumber,
  stripPhoneFormat,
} from "../../../utils/formatters";
import { HandleInput } from "../../../components/HandleInput";
import { emptyParent } from "./usePlayerFormState";
import type {
  PlayerFormErrors,
  PlayerFormSetters,
  PlayerFormState,
} from "./playerFormTypes";

const CURRENT_YEAR = new Date().getFullYear();
const GRAD_YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR + i);
const RECRUITMENT_STAGE_OPTIONS = ["Foundational", "Promotional", "Decision"];

interface Props {
  state: PlayerFormState;
  setters: PlayerFormSetters;
  errors: PlayerFormErrors;
  disabled?: boolean;
  showRecruitmentStage?: boolean;
  showProjectedDivisions?: boolean;
}

export const PlayerFormSections = ({
  state,
  setters,
  errors,
  disabled,
  showRecruitmentStage = false,
  showProjectedDivisions = false,
}: Props) => {
  const { set, setParents, setParentDraft, updateParentDraft, togglePreferredLocation, clearError } =
    setters;

  // Unique per mounted form so ids stay distinct if this ever renders twice on a page.
  const uid = useId();
  const fieldId = (name: string) => `${uid}-${name}`;
  const errorId = (name: string) => `${uid}-${name}-error`;

  const onChange = <K extends keyof PlayerFormState>(
    field: K,
    errorKey?: string
  ) => (value: PlayerFormState[K]) => {
    set(field, value);
    clearError(errorKey ?? (field as string));
  };

  const renderField = (
    label: string,
    field: keyof PlayerFormState,
    opts?: {
      type?: string;
      placeholder?: string;
      required?: boolean;
      errorKey?: string;
      /**
       * WCAG 1.3.5. Only set on fields collecting the submitter's own details:
       * autofilling a parent's name with the player's would be wrong.
       */
      autoComplete?: string;
    }
  ) => {
    const errKey = opts?.errorKey ?? (field as string);
    const value = state[field] as string;
    const id = fieldId(field as string);
    const err = errors[errKey];
    return (
      <div className="submission-field">
        <label className="submission-label" htmlFor={id}>
          {label}
          {opts?.required ? " *" : ""}
        </label>
        <input
          id={id}
          className={`submission-input${err ? " submission-input--error" : ""}`}
          type={opts?.type || "text"}
          value={value}
          onChange={(e) => {
            set(field, e.target.value as PlayerFormState[typeof field]);
            clearError(errKey);
          }}
          placeholder={opts?.placeholder}
          disabled={disabled}
          required={opts?.required}
          autoComplete={opts?.autoComplete}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? errorId(errKey) : undefined}
        />
        {err && (
          <span className="submission-field-error" id={errorId(errKey)}>
            {err}
          </span>
        )}
      </div>
    );
  };

  const renderPhoneField = (
    label: string,
    field: keyof PlayerFormState,
    opts?: { required?: boolean; autoComplete?: string }
  ) => {
    const key = field as string;
    const id = fieldId(key);
    const err = errors[key];
    return (
      <div className="submission-field">
        <label className="submission-label" htmlFor={id}>
          {label}
          {opts?.required ? " *" : ""}
        </label>
        <input
          id={id}
          className={`submission-input${err ? " submission-input--error" : ""}`}
          type="tel"
          value={state[field] as string}
          disabled={disabled}
          onChange={(e) => {
            set(field, formatPhoneNumber(e.target.value) as PlayerFormState[typeof field]);
            clearError(key);
          }}
          placeholder="(555) 123-4567"
          required={opts?.required}
          autoComplete={opts?.autoComplete}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? errorId(key) : undefined}
        />
        {err && (
          <span className="submission-field-error" id={errorId(key)}>
            {err}
          </span>
        )}
      </div>
    );
  };

  const renderHandleField = (
    label: string,
    field: "xHandle" | "instagramUrl" | "schoolTwitter" | "travelTeamTwitter",
    placeholder?: string
  ) => (
    <HandleInput
      id={fieldId(field)}
      label={label}
      value={state[field]}
      onChange={onChange(field)}
      error={errors[field]}
      disabled={disabled}
      placeholder={placeholder}
      variant="submission"
      wrapperClassName="submission-field"
    />
  );

  const draft = state.parentDraft;
  const isDraftValid =
    draft != null &&
    draft.firstName.trim() !== "" &&
    draft.lastName.trim() !== "" &&
    (draft.email.trim() !== "" || stripPhoneFormat(draft.phone).length > 0);

  const commitParent = () => {
    if (!draft || !isDraftValid) return;
    setParents([...state.parents, draft]);
    setParentDraft(null);
  };

  const removeParent = (index: number) => {
    setParents(state.parents.filter((_, i) => i !== index));
  };

  return (
    <>
      {/* PERSONAL INFORMATION */}
      <section className="submission-section">
        <h2 className="submission-section__title">Personal Information</h2>
        <div className="submission-row">
          {renderField("First Name", "firstName", {
            required: true,
            autoComplete: "given-name",
          })}
          {renderField("Last Name", "lastName", {
            required: true,
            autoComplete: "family-name",
          })}
        </div>
        <div className="submission-row">
          {renderField("Email", "email", {
            type: "email",
            required: true,
            autoComplete: "email",
          })}
          {renderPhoneField("Phone", "phone", { required: true, autoComplete: "tel" })}
        </div>
        <div className="submission-row">
          {renderHandleField("X Handle", "xHandle", "handle")}
          {renderHandleField("Instagram", "instagramUrl", "handle")}
        </div>
        <div className="submission-row">
          {renderField("Slack Username", "slackUsername", { placeholder: "username or email" })}
        </div>
      </section>

      {/* LINKS */}
      <section className="submission-section">
        <h2 className="submission-section__title">Links</h2>
        <div className="submission-row">
          {renderField("Video Link", "videoLink", { placeholder: "https://..." })}
          {renderField("Scouting Profile Link", "scoutingProfileLink", {
            placeholder: "https://...",
          })}
        </div>
      </section>

      {/* PLAYER DETAILS */}
      <section className="submission-section">
        <h2 className="submission-section__title">Player Details</h2>
        <div className="submission-field">
          <label className="submission-label" htmlFor={fieldId("position")}>
            Main Position *
          </label>
          <select
            id={fieldId("position")}
            className={`submission-select${errors.position ? " submission-select--error" : ""}`}
            value={state.position}
            disabled={disabled}
            required
            aria-invalid={errors.position ? true : undefined}
            aria-describedby={errors.position ? errorId("position") : undefined}
            onChange={(e) => {
              set("position", e.target.value);
              clearError("position");
            }}
          >
            <option value="">Select position...</option>
            {POSITION_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          {errors.position && (
            <span className="submission-field-error" id={errorId("position")}>
              {errors.position}
            </span>
          )}
        </div>
        <div className="submission-row">
          <div className="submission-field">
            <label className="submission-label" htmlFor={fieldId("bats")}>
              Bats *
            </label>
            <select
              id={fieldId("bats")}
              className={`submission-select${errors.bats ? " submission-select--error" : ""}`}
              value={state.bats}
              disabled={disabled}
              required
              aria-invalid={errors.bats ? true : undefined}
              aria-describedby={errors.bats ? errorId("bats") : undefined}
              onChange={(e) => {
                set("bats", e.target.value);
                clearError("bats");
              }}
            >
              <option value="">L / R</option>
              <option value="L">L</option>
              <option value="R">R</option>
            </select>
            {errors.bats && (
              <span className="submission-field-error" id={errorId("bats")}>
                {errors.bats}
              </span>
            )}
          </div>
          <div className="submission-field">
            <label className="submission-label" htmlFor={fieldId("throws")}>
              Throws *
            </label>
            <select
              id={fieldId("throws")}
              className={`submission-select${errors.throws ? " submission-select--error" : ""}`}
              value={state.throws}
              disabled={disabled}
              required
              aria-invalid={errors.throws ? true : undefined}
              aria-describedby={errors.throws ? errorId("throws") : undefined}
              onChange={(e) => {
                set("throws", e.target.value);
                clearError("throws");
              }}
            >
              <option value="">L / R</option>
              <option value="L">L</option>
              <option value="R">R</option>
            </select>
            {errors.throws && (
              <span className="submission-field-error" id={errorId("throws")}>
                {errors.throws}
              </span>
            )}
          </div>
        </div>
        <div className="submission-row submission-row--three">
          {renderField("Height (ft)", "heightFeet", {
            type: "number",
            placeholder: "6",
            required: true,
          })}
          <div className="submission-field">
            <label className="submission-label" htmlFor={fieldId("heightInches")}>
              Height (in)
            </label>
            <input
              id={fieldId("heightInches")}
              className="submission-input"
              type="number"
              value={state.heightInches}
              disabled={disabled}
              onChange={(e) => set("heightInches", e.target.value)}
              placeholder="1"
            />
          </div>
          {renderField("Weight (lbs)", "weight", {
            type: "number",
            placeholder: "185",
            required: true,
          })}
        </div>
      </section>

      {/* HIGH SCHOOL */}
      <section className="submission-section">
        <h2 className="submission-section__title">High School</h2>
        <div className="submission-row">
          {renderField("School", "school", { required: true })}
          <div className="submission-field">
            <label className="submission-label" htmlFor={fieldId("gradYear")}>
              Grad Year *
            </label>
            <select
              id={fieldId("gradYear")}
              className="submission-select"
              value={state.gradYear}
              disabled={disabled}
              required
              onChange={(e) => set("gradYear", e.target.value)}
            >
              {GRAD_YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="submission-row">
          {renderField("City", "city", { required: true, autoComplete: "address-level2" })}
          <div className="submission-field">
            <label className="submission-label" htmlFor={fieldId("state")}>
              State *
            </label>
            <select
              id={fieldId("state")}
              className={`submission-select${errors.state ? " submission-select--error" : ""}`}
              value={state.state}
              disabled={disabled}
              required
              autoComplete="address-level1"
              aria-invalid={errors.state ? true : undefined}
              aria-describedby={errors.state ? errorId("state") : undefined}
              onChange={(e) => {
                set("state", e.target.value);
                clearError("state");
              }}
            >
              <option value="">Select state...</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {errors.state && (
              <span className="submission-field-error" id={errorId("state")}>
                {errors.state}
              </span>
            )}
          </div>
        </div>
        <div className="submission-row">
          {renderField("GPA", "gpa", { placeholder: "3.5" })}
          {renderField("Intended Major", "intendedMajor")}
        </div>
        <div className="submission-row">
          {renderField("Head Coach Name", "coachName")}
          {renderPhoneField("Head Coach Phone", "coachPhone")}
        </div>
        <div className="submission-row">
          {renderHandleField("HS Program Twitter", "schoolTwitter", "handle")}
        </div>
      </section>

      {/* TRAVEL BALL */}
      <section className="submission-section">
        <h2 className="submission-section__title">Travel Ball</h2>
        {renderField("Travel Team Name", "travelTeam")}
        <div className="submission-row">
          {renderField("Coach Name", "travelTeamCoachName")}
          {renderPhoneField("Coach Phone", "travelTeamCoachPhone")}
        </div>
        <div className="submission-row">
          {renderHandleField("Team Twitter", "travelTeamTwitter", "handle")}
        </div>
      </section>

      {/* RECRUITING */}
      <section className="submission-section">
        <h2 className="submission-section__title">Recruiting</h2>
        <div className="submission-field">
          {/*
           * A toggle-button group, not a labelled control, so the caption is a
           * plain span the group points at. A bare <label> here would have no
           * form control to name.
           */}
          <span className="submission-label" id={fieldId("preferredLocations-label")}>
            Preferred Locations * (up to 3)
          </span>
          <div
            className="submission-state-chips"
            role="group"
            aria-labelledby={fieldId("preferredLocations-label")}
            aria-describedby={
              errors.preferredLocations ? errorId("preferredLocations") : undefined
            }
          >
            {US_STATES.map((s) => {
              const selected = state.preferredLocations.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={selected}
                  className={`submission-state-chip${
                    selected ? " submission-state-chip--selected" : ""
                  }`}
                  onClick={() => togglePreferredLocation(s)}
                  disabled={
                    disabled || (!selected && state.preferredLocations.length >= 3)
                  }
                >
                  {s}
                </button>
              );
            })}
          </div>
          {errors.preferredLocations && (
            <span className="submission-field-error" id={errorId("preferredLocations")}>
              {errors.preferredLocations}
            </span>
          )}
        </div>
        {renderField("End Goal", "endGoal", {
          placeholder: "e.g., Play D1 baseball, get a scholarship",
        })}
        {showRecruitmentStage && (
          <div className="submission-field">
            <label className="submission-label" htmlFor={fieldId("recruitmentStage")}>
              Recruitment Stage
            </label>
            <select
              id={fieldId("recruitmentStage")}
              className="submission-select"
              value={state.recruitmentStage}
              disabled={disabled}
              onChange={(e) => set("recruitmentStage", e.target.value)}
            >
              <option value="">Select stage...</option>
              {RECRUITMENT_STAGE_OPTIONS.map((rs) => (
                <option key={rs} value={rs}>
                  {rs}
                </option>
              ))}
            </select>
          </div>
        )}
        {showProjectedDivisions && (
          <div className="submission-field">
            <span className="submission-label" id={fieldId("projectedDivisions-label")}>
              Projected Divisions (up to 3)
            </span>
            <div
              className="submission-state-chips"
              role="group"
              aria-labelledby={fieldId("projectedDivisions-label")}
            >
              {DIVISIONS.map((d) => {
                const selected = state.projectedDivisions.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    aria-pressed={selected}
                    className={`submission-state-chip${
                      selected ? " submission-state-chip--selected" : ""
                    }`}
                    disabled={disabled || (!selected && state.projectedDivisions.length >= 3)}
                    onClick={() => {
                      const next = selected
                        ? state.projectedDivisions.filter((x) => x !== d)
                        : state.projectedDivisions.length >= 3
                          ? state.projectedDivisions
                          : [...state.projectedDivisions, d];
                      set("projectedDivisions", next);
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* PARENTS */}
      <section className="submission-section">
        <h2 className="submission-section__title">Parents / Guardians</h2>
        {state.parents.map((p, i) => (
          <div key={i} className="submission-parent-chip">
            <div className="submission-parent-chip-info">
              <span className="submission-parent-chip-name">
                {p.firstName} {p.lastName}
              </span>
              <span className="submission-parent-chip-detail">
                {p.email || stripPhoneFormat(p.phone)}
              </span>
            </div>
            <button
              type="button"
              className="submission-parent-remove"
              onClick={() => removeParent(i)}
              disabled={disabled}
              aria-label={`Remove ${p.firstName} ${p.lastName}`}
            >
              Remove
            </button>
          </div>
        ))}

        {draft && (
          <div className="submission-parent-card">
            {/*
             * No autoComplete on this block: these fields describe a parent or
             * guardian, not the person filling in the form, so browser autofill
             * would put the player's own details in the wrong place.
             */}
            <div className="submission-row">
              <div className="submission-field">
                <label className="submission-label" htmlFor={fieldId("parent-firstName")}>
                  First Name *
                </label>
                <input
                  id={fieldId("parent-firstName")}
                  className="submission-input"
                  value={draft.firstName}
                  onChange={(e) => updateParentDraft("firstName", e.target.value)}
                  disabled={disabled}
                  required
                />
              </div>
              <div className="submission-field">
                <label className="submission-label" htmlFor={fieldId("parent-lastName")}>
                  Last Name *
                </label>
                <input
                  id={fieldId("parent-lastName")}
                  className="submission-input"
                  value={draft.lastName}
                  onChange={(e) => updateParentDraft("lastName", e.target.value)}
                  disabled={disabled}
                  required
                />
              </div>
            </div>
            <div className="submission-row">
              <div className="submission-field">
                <label className="submission-label" htmlFor={fieldId("parent-email")}>
                  Email *
                </label>
                <input
                  id={fieldId("parent-email")}
                  className="submission-input"
                  type="email"
                  value={draft.email}
                  onChange={(e) => updateParentDraft("email", e.target.value)}
                  disabled={disabled}
                  required
                />
              </div>
              <div className="submission-field">
                <label className="submission-label" htmlFor={fieldId("parent-phone")}>
                  Phone *
                </label>
                <input
                  id={fieldId("parent-phone")}
                  className="submission-input"
                  type="tel"
                  value={draft.phone}
                  onChange={(e) =>
                    updateParentDraft("phone", formatPhoneNumber(e.target.value))
                  }
                  placeholder="(555) 123-4567"
                  disabled={disabled}
                  required
                />
              </div>
            </div>
            <div className="submission-row">
              <HandleInput
                id={fieldId("parent-xHandle")}
                label="X Username"
                value={draft.xHandle}
                onChange={(v) => updateParentDraft("xHandle", v)}
                disabled={disabled}
                placeholder="handle"
                variant="submission"
                wrapperClassName="submission-field"
              />
              <div className="submission-field">
                <label className="submission-label" htmlFor={fieldId("parent-slackUsername")}>
                  Slack Username
                </label>
                <input
                  id={fieldId("parent-slackUsername")}
                  className="submission-input"
                  type="text"
                  value={draft.slackUsername}
                  disabled={disabled}
                  onChange={(e) => updateParentDraft("slackUsername", e.target.value)}
                  placeholder="username or email"
                />
              </div>
            </div>
            <div className="submission-parent-actions">
              <button
                type="button"
                className="submission-parent-cancel"
                onClick={() => setParentDraft(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="submission-parent-commit"
                disabled={!isDraftValid}
                onClick={commitParent}
              >
                Add
              </button>
            </div>
          </div>
        )}

        {state.parents.length < 2 && !draft && (
          <button
            type="button"
            className="submission-add-parent-btn"
            onClick={() => setParentDraft(emptyParent())}
            disabled={disabled}
          >
            + Add Parent
          </button>
        )}
      </section>

      {/* NOTES */}
      <section className="submission-section">
        <h2 className="submission-section__title" id={fieldId("notes-label")}>
          Additional Notes
        </h2>
        <div className="submission-field">
          {/*
           * The section heading is the only visible caption for this field, so
           * it doubles as the accessible name. A second visible label would just
           * repeat "Additional Notes" directly under the heading.
           */}
          <textarea
            id={fieldId("notes")}
            aria-labelledby={fieldId("notes-label")}
            className="submission-textarea"
            placeholder="Anything else you'd like us to know..."
            rows={4}
            value={state.notes}
            onChange={(e) => set("notes", e.target.value)}
            disabled={disabled}
          />
        </div>
      </section>
    </>
  );
};
